import React from "react";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import CustomDateField from "../CustomDateField";
import CustomLabel from "../CustomLabel";
import { COLORS } from "../../assets/theme";
import { endClass } from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { ClassDefinition, EndClassResult } from "./types";
import { formatTime, sessionRowKey, toDateInputValue } from "./classUtils";

interface EndClassSeriesDialogProps {
  open: boolean;
  classItem: ClassDefinition | null;
  onClose: () => void;
  onEnded: () => void;
}

/**
 * Stops an open-ended class as of a date. Everything already materialized past
 * that date is cancelled properly — package credits refunded, seats returned,
 * invoices voided — and every affected client is messaged by the backend, the
 * same way deactivating the class does it. `clients_to_notify` is the record of
 * who that reached, so the result screen reports it rather than asking staff to
 * chase people who have already heard.
 */
const EndClassSeriesDialog: React.FC<EndClassSeriesDialogProps> = ({
  open,
  classItem,
  onClose,
  onEnded,
}) => {
  const [endDate, setEndDate] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState<EndClassResult | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setEndDate(toDateInputValue(new Date()));
    setResult(null);
  }, [open]);

  const handleConfirm = async () => {
    if (!classItem) return;
    setSubmitting(true);
    try {
      const response = await endClass(String(classItem.id), endDate || undefined);
      setResult(response);
      toast.success(
        `"${classItem.name || "Class"}" ends ${formatTableDate(
          response.end_date || endDate
        )}.`
      );
      onEnded();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to end the class."));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelled = result?.cancelled_sessions || [];
  const toNotify = result?.clients_to_notify || [];

  const close = () => {
    setResult(null);
    onClose();
  };

  return (
    <CustomDialog
      open={open}
      onClose={() => {
        if (!submitting) close();
      }}
      paperSx={{ minWidth: { sm: 540 }, maxWidth: 700 }}
      title={result ? "Class ended" : `End — ${classItem?.name || "Class"}`}
      actions={
        result ? (
          <CustomButton
            label="Done"
            variant="contained"
            bgColor={COLORS.MainColor}
            textColor={COLORS.WhiteText}
            onClick={close}
          />
        ) : (
          <>
            <CustomButton
              label="Keep running"
              variant="outlined"
              onClick={close}
              disabled={submitting}
            />
            <CustomButton
              label="End class"
              variant="contained"
              bgColor="#d32f2f"
              textColor="#fff"
              loading={submitting}
              disabled={submitting || !endDate}
              onClick={handleConfirm}
            />
          </>
        )
      }
    >
      {!result ? (
        <>
          <Typography sx={{ fontSize: 14, mb: 2 }}>
            No sessions run after this date. Anything already booked past it is
            cancelled — package credits are refunded, seats returned and
            invoices voided.
          </Typography>
          <CustomLabel
            text="Last day the class runs"
            fontSize={14}
            fontWeight={500}
            highlightLabelStar
          />
          <CustomDateField
            value={endDate}
            onChange={(value: string) => setEndDate(String(value || ""))}
            fullWidth
          />
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: 14, mb: 2 }}>
            {`Ends ${formatTableDate(result.end_date || endDate)} — ${
              cancelled.length
            } booked session${
              cancelled.length === 1 ? "" : "s"
            } past that date ${
              cancelled.length === 1 ? "was" : "were"
            } cancelled and refunded.`}
          </Typography>

          {cancelled.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {cancelled.slice(0, 8).map((session, index) => (
                <Typography
                  key={sessionRowKey(session, index)}
                  sx={{ fontSize: 13, color: "#666" }}
                >
                  {`• ${formatTableDate(session.start_at)} · ${formatTime(
                    session.start_at
                  )}`}
                </Typography>
              ))}
              {cancelled.length > 8 && (
                <Typography sx={{ fontSize: 12, color: "#999" }}>
                  {`+ ${cancelled.length - 8} more`}
                </Typography>
              )}
            </Box>
          )}

          {toNotify.length === 0 ? (
            <Typography sx={{ fontSize: 14, color: "#2e7d32" }}>
              Nobody was booked past that date, so there is nobody to contact.
            </Typography>
          ) : (
            <Box>
              <Typography
                sx={{ fontSize: 14, fontWeight: 700, color: "#2e7d32", mb: 0.5 }}
              >
                {`${toNotify.length} client${
                  toNotify.length === 1 ? " was" : "s were"
                } notified`}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#4a6b4d", mb: 1.5 }}>
                Sent automatically when the sessions were cancelled — there is
                nothing for you to send.
              </Typography>
              {toNotify.map((entry, index) => (
                <Box
                  key={String(entry.id || index)}
                  sx={{
                    border: "1px solid #e2e8e3",
                    bgcolor: "#f7faf8",
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    {entry.full_name || "Client"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#5a6b5c" }}>
                    {[entry.phone, entry.country].filter(Boolean).join(" · ") ||
                      "No contact details on file"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </CustomDialog>
  );
};

export default EndClassSeriesDialog;
